/**
 * Esquemas Zod: unica fuente de verdad del dominio.
 *
 * Los tipos de TypeScript se DERIVAN de estos esquemas con `z.infer`. No se
 * escribe el mismo tipo dos veces: si el esquema y la interfaz se declararan por
 * separado, tarde o temprano divergen y la validacion deja de proteger nada.
 *
 * Todo cuerpo, query y parametro que entra al servidor se valida contra uno de
 * estos esquemas antes de tocar la base de datos.
 */
import { z } from 'zod';
import { ROLES } from './permissions.js';
import { INTERNAL_PREFIXES, SCALE_MODES } from './barcode.js';
// ---------------------------------------------------------------------------
// Primitivas del dominio
// ---------------------------------------------------------------------------
/** Identificador de fila. SQLite usa enteros autoincrementales. */
export const idSchema = z.number().int().positive();
/** Importe en centavos. Nunca negativo salvo donde se indique explicitamente. */
export const centsSchema = z.number().int().min(0).max(2 ** 52);
/** Importe en centavos que admite signo (ajustes, diferencias de caja). */
export const signedCentsSchema = z.number().int().min(-(2 ** 52)).max(2 ** 52);
/** Cantidad en milesimas. 1000 === una unidad completa. */
export const milliSchema = z.number().int().min(0).max(2 ** 40);
export const signedMilliSchema = z.number().int().min(-(2 ** 40)).max(2 ** 40);
/** Fecha de negocio `YYYY-MM-DD`. */
export const businessDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'la fecha debe tener el formato AAAA-MM-DD');
export const timestampSchema = z.number().int().nonnegative();
const trimmed = (min, max) => z.string().trim().min(min).max(max);
// ---------------------------------------------------------------------------
// Catalogos cerrados
// ---------------------------------------------------------------------------
/**
 * Como se cobra un producto.
 *
 * Las tres ultimas son de BODEGA y no existen en una tienda de abarrotes: que
 * cada sucursal use unas u otras lo decide `stores.unit_profile`, no esta lista.
 * Aqui estan juntas porque el enum es de la base de datos, que es una sola.
 */
export const UNITS = ['pz', 'kg', 'g', 'l', 'ml', 'paq', 'caja', 'ton', 'b25', 'b50'];
export const unitSchema = z.enum(UNITS);
export const UNIT_LABELS = {
    pz: 'Pieza',
    kg: 'Kilogramo',
    g: 'Gramo',
    l: 'Litro',
    ml: 'Mililitro',
    paq: 'Paquete',
    caja: 'Caja',
    ton: 'Tonelada',
    b25: 'Bulto 25 kg',
    b50: 'Bulto 50 kg',
};
/**
 * Como se dice la unidad al hablar, para meterla dentro de una frase.
 *
 * "Lo que te cuesta por kilo" se lee natural; "por kg" o "por Kilogramo", no.
 * Las etiquetas de arriba son para listas y selectores; estas, para texto
 * corrido dirigido a la persona que usa el sistema.
 */
export const UNIT_SPOKEN = {
    pz: 'pieza',
    kg: 'kilo',
    g: 'gramo',
    l: 'litro',
    ml: 'mililitro',
    paq: 'paquete',
    caja: 'caja',
    ton: 'tonelada',
    b25: 'bulto de 25 kilos',
    b50: 'bulto de 50 kilos',
};
/**
 * La abreviatura que va PEGADA A UN NUMERO: "200 b50", "1.5 t", "3 kg".
 *
 * Para las siete de menudeo es el propio codigo, que es justo lo que la interfaz
 * venia imprimiendo crudo — asi que cambiar `{item.unit}` por esta tabla no mueve
 * un pixel en Centro, y esa es la red de la sustitucion. Existe por las tres de
 * bodega: "200 bulto de 50 kilos" no cabe en una celda y "200 b50" si.
 */
export const UNIT_SHORT = {
    pz: 'pz',
    kg: 'kg',
    g: 'g',
    l: 'l',
    ml: 'ml',
    paq: 'paq',
    caja: 'caja',
    ton: 't',
    b25: 'b25',
    b50: 'b50',
};
/** Unidades que implican pesar o medir, es decir, cantidad con decimales. */
export const WEIGHED_UNITS = ['kg', 'g', 'l', 'ml', 'ton'];
/**
 * Que se vende en una sucursal.
 *
 * `retail` es la tienda de abarrotes: pieza, kilo, litro, paquete, caja.
 * `bulk` es la bodega: solo tonelada y bulto de 25 y 50 kg.
 *
 * NO es una preferencia de la interfaz. Lo aplica la base con un trigger sobre
 * `products`, porque un catalogo con unidades que en esa sucursal no existen
 * produce cifras que significan una cosa aqui y otra en la sucursal de al lado.
 * Esto es el espejo de `stores.unit_profile` y sirve para esconder controles,
 * igual que `permissions.ts` es el espejo de `role_permissions`.
 */
export const UNIT_PROFILES = ['retail', 'bulk'];
export const unitProfileSchema = z.enum(UNIT_PROFILES);
export const UNIT_PROFILE_LABELS = {
    retail: 'Tienda de abarrotes',
    bulk: 'Bodega',
};
/** Que se cobra en cada tipo de sucursal, dicho como lo diria una persona. */
export const UNIT_PROFILE_HINTS = {
    retail: 'Se cobra por pieza, kilo, litro, paquete o caja.',
    bulk: 'Se cobra por tonelada o por bulto de 25 y 50 kg.',
};
export const PAYMENT_METHODS = ['cash', 'card', 'transfer', 'credit'];
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const PAYMENT_METHOD_LABELS = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    credit: 'Fiado',
};
export const SALE_STATUSES = ['completed', 'cancelled'];
export const saleStatusSchema = z.enum(SALE_STATUSES);
/**
 * Tipos de movimiento de inventario (kardex).
 * El kardex es la verdad historica; `store_products.stock_milli` es solo caché.
 */
export const MOVEMENT_TYPES = [
    'initial', // carga inicial de existencias
    'sale', // salida por venta
    'sale_cancel', // reingreso por cancelacion de venta
    'purchase', // entrada por compra a proveedor
    'purchase_return', // devolucion al proveedor
    'adjustment', // faltante o sobrante SIN explicar, detectado al contar
    'shrinkage', // merma declarada: caducado, roto, robado
    'natural_loss', // deshidratacion del granel: normal y esperada
];
export const movementTypeSchema = z.enum(MOVEMENT_TYPES);
export const MOVEMENT_TYPE_LABELS = {
    initial: 'Carga inicial',
    sale: 'Venta',
    sale_cancel: 'Cancelacion de venta',
    purchase: 'Compra',
    purchase_return: 'Devolucion a proveedor',
    adjustment: 'Faltante sin explicar',
    shrinkage: 'Merma declarada',
    natural_loss: 'Merma natural',
};
/**
 * Tasa de merma natural mensual, en PUNTOS BASE (350 = 3.50 % al mes).
 *
 * Se guarda como entero por la misma razon que el dinero: un porcentaje en
 * punto flotante multiplicado por un peso y por unos dias acumula error, y aqui
 * el resultado decide si se acusa a alguien de un faltante.
 */
export const shrinkageBpSchema = z.number().int().min(0).max(5000);
/** Puntos base a porcentaje legible. Solo para mostrar. */
export function bpToPct(bp) {
    return bp / 100;
}
/** Porcentaje capturado por el usuario a puntos base. */
export function pctToBp(pct) {
    return Math.round(pct * 100);
}
export const EXPENSE_CATEGORIES = [
    'rent',
    'payroll',
    'utilities',
    'supplies',
    'maintenance',
    'transport',
    'fees',
    'other',
];
export const expenseCategorySchema = z.enum(EXPENSE_CATEGORIES);
export const EXPENSE_CATEGORY_LABELS = {
    rent: 'Renta',
    payroll: 'Sueldos',
    utilities: 'Luz, agua y gas',
    supplies: 'Insumos y limpieza',
    maintenance: 'Mantenimiento',
    transport: 'Transporte y fletes',
    fees: 'Comisiones y permisos',
    other: 'Otros',
};
/**
 * Cada cuando vuelve el gasto.
 *
 * Es un dato del COMPROMISO, no del movimiento: el renglon sigue siendo un pago
 * con su importe y su fecha, y los reportes lo suman igual. Sirve para leer la
 * lista y distinguir lo que se paga una vez de lo que es costo fijo.
 *
 * La lista sale de como se paga en una tienda de abarrotes en Mexico: los
 * sueldos por semana o por quincena, la renta al mes, la luz de CFE cada dos
 * meses, los permisos al año.
 */
export const EXPENSE_RECURRENCES = [
    'one_time',
    'weekly',
    'biweekly',
    'monthly',
    'bimonthly',
    'annual',
];
export const expenseRecurrenceSchema = z.enum(EXPENSE_RECURRENCES);
export const EXPENSE_RECURRENCE_LABELS = {
    one_time: 'Una sola vez',
    weekly: 'Semanal',
    biweekly: 'Quincenal',
    monthly: 'Mensual',
    bimonthly: 'Bimestral',
    annual: 'Anual',
};
export const CASH_MOVEMENT_TYPES = ['in', 'out'];
export const cashMovementTypeSchema = z.enum(CASH_MOVEMENT_TYPES);
export const roleSchema = z.enum(ROLES);
// ---------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------
export const storeSchema = z.object({
    id: idSchema,
    name: trimmed(1, 80),
    code: trimmed(1, 16),
    address: z.string().max(200).nullable(),
    phone: z.string().max(30).nullable(),
    timezone: z.string().min(1).max(64),
    dayStartHour: z.number().int().min(0).max(23),
    /** Redondeo del total en efectivo. 0 lo desactiva, 50 redondea a medio peso. */
    cashRoundingCents: z.number().int().min(0).max(1000),
    /** Prefijo con el que la sucursal acuña sus codigos internos (rango 20-29). */
    internalBarcodePrefix: z.enum(INTERNAL_PREFIXES),
    /** Configuracion de la bascula etiquetadora, si la hay. */
    scale: z.object({
        mode: z.enum(SCALE_MODES),
        prefix: z.string().min(1).max(3),
        itemDigits: z.number().int().min(1).max(10),
        valueDigits: z.number().int().min(1).max(10),
    }),
    isActive: z.boolean(),
    createdAt: timestampSchema,
});
/**
 * El expediente de una sucursal: lo que lleva encima y que pasaria al eliminarla.
 *
 * Lo arma la base (`get_store_usage`) porque es la unica que ve el negocio
 * entero: en el navegador, "creo que esa no vendio nada" no es un dato. Mismo
 * papel que `ProductUsage` para un producto, y por el mismo motivo — la pantalla
 * no decide, traduce.
 */
export const storeUsageSchema = z.object({
    storeId: idSchema,
    name: z.string(),
    code: z.string(),
    isActive: z.boolean(),
    /** Ventas COBRADAS. Las canceladas no cuentan como uso del negocio. */
    salesCount: z.number().int().nonnegative(),
    /** Lo vendido en toda su vida. `null` sin `sale:view_all`: nulo no es cero. */
    revenueCents: centsSchema.nullable(),
    /** Fecha de negocio de la ultima venta, `YYYY-MM-DD`. */
    lastSaleDate: z.string().nullable(),
    purchaseCount: z.number().int().nonnegative(),
    /** Activos y descontinuados: los dos son captura de alguien. */
    productCount: z.number().int().nonnegative(),
    shiftCount: z.number().int().nonnegative(),
    openShiftCount: z.number().int().nonnegative(),
    expenseCount: z.number().int().nonnegative(),
    creditCount: z.number().int().nonnegative(),
    intakeCount: z.number().int().nonnegative(),
    staffCount: z.number().int().nonnegative(),
    /** Si tiene cualquier huella. Es lo que decide `deleteMode`. */
    hasHistory: z.boolean(),
    /** Permiso de quien pregunta, a nivel NEGOCIO: el mismo con el que se abren. */
    mayDelete: z.boolean(),
    /**
     * Los dos motivos por los que no se puede, separados para que la pantalla
     * nombre el que aplica. Un aviso generico obliga a adivinar cual de los dos es.
     */
    isLastActive: z.boolean(),
    hasOpenShift: z.boolean(),
    /** Permiso Y ninguno de los dos bloqueos. Lo unico que habilita eliminar. */
    canDelete: z.boolean(),
    /**
     * QUE va a pasar al eliminar. Lo decide la base mirando el expediente.
     *
     *   'clean'   Nunca se uso. Se va de raiz, como si no se hubiera abierto.
     *   'history' Ya se uso. Se cierra y desaparece de todas las pantallas; sus
     *             ventas, cortes, gastos y fiado quedan intactos, porque borrarlos
     *             cambiaria cuanto vendio el NEGOCIO.
     */
    deleteMode: z.enum(['clean', 'history']),
});
export const updateStoreBarcodeRequestSchema = z.object({
    internalBarcodePrefix: z.enum(INTERNAL_PREFIXES).optional(),
    scaleMode: z.enum(SCALE_MODES).optional(),
    scalePrefix: z.string().trim().regex(/^\d{1,3}$/, 'el prefijo son de 1 a 3 digitos').optional(),
    scaleItemDigits: z.number().int().min(1).max(10).optional(),
    scaleValueDigits: z.number().int().min(1).max(10).optional(),
});
/**
 * Identificador de usuario.
 *
 * Es un UUID y no un entero porque la identidad la emite Supabase Auth: la
 * tabla `users` del dominio es 1:1 con `auth.users` y comparte su llave. El
 * resto de las entidades del negocio (productos, ventas, sucursales) siguen
 * usando enteros consecutivos, que son mas comodos de leer en un folio.
 */
export const userIdSchema = z.uuid();
export const userSchema = z.object({
    id: userIdSchema,
    /** Nulo cuando la cuenta se creo solo con correo, sin alias de mostrador. */
    username: trimmed(3, 32).nullable(),
    fullName: trimmed(1, 80),
    isActive: z.boolean(),
    createdAt: timestampSchema,
});
export const storeMembershipSchema = z.object({
    storeId: idSchema,
    storeName: z.string(),
    storeCode: z.string(),
    role: roleSchema,
    /**
     * Que se vende en esa sucursal. Viaja en la SESION y no en una consulta aparte
     * porque lo necesita cada pantalla que dibuje o lea una unidad, y la membresia
     * ya se carga una sola vez. Ojo: es para esconder controles; quien de verdad
     * lo aplica es la base.
     */
    unitProfile: unitProfileSchema,
});
/** Lo que el cliente recibe al iniciar sesion. */
export const sessionUserSchema = userSchema.extend({
    stores: z.array(storeMembershipSchema),
});
export const categorySchema = z.object({
    id: idSchema,
    name: trimmed(1, 60),
    /** Color de acento en la cuadricula tactil de la caja. */
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    sortOrder: z.number().int(),
});
export const productSchema = z.object({
    id: idSchema,
    sku: trimmed(1, 40),
    name: trimmed(1, 120),
    brand: z.string().max(60).nullable(),
    categoryId: idSchema.nullable(),
    unit: unitSchema,
    /** True para granel: la cantidad admite decimales y el precio es por unidad de medida. */
    isWeighed: z.boolean(),
    /** Piezas por caja/paquete del proveedor. Redondea las sugerencias de compra. */
    packSize: z.number().int().min(1).max(10_000),
    /**
     * Cuanto peso pierde al mes por deshidratacion, en puntos base.
     * 0 para todo lo empaquetado; el granel seco ronda 100-400.
     */
    shrinkageBpMonthly: shrinkageBpSchema,
    /** Numero con que la bascula etiquetadora conoce a este producto. */
    scaleItemCode: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
/**
 * Precio y existencias de un producto EN UNA SUCURSAL.
 * Un mismo producto puede costar distinto en cada tienda.
 */
export const storeProductSchema = z.object({
    storeId: idSchema,
    productId: idSchema,
    priceCents: centsSchema,
    /**
     * Segundo precio, OPCIONAL, que la caja puede elegir renglon por renglon.
     *
     * `null` significa "este producto no se vende a mayoreo", y no es lo mismo que
     * cero —cero seria venderlo regalado—. El sistema NUNCA lo aplica solo: no hay
     * minimo de cantidad ni clientes marcados. Lo decide quien esta viendo al
     * cliente, y el sistema pone lo unico que esa persona no debe improvisar, que
     * es el numero.
     */
    wholesalePriceCents: centsSchema.nullable(),
    /** Costo promedio ponderado, recalculado en cada compra. */
    costCents: centsSchema,
    stockMilli: signedMilliSchema,
    /** Existencia minima antes de disparar alerta. 0 = sin alerta. */
    minStockMilli: milliSchema,
    /** Existencia objetivo al resurtir. La sugerencia de compra apunta aqui. */
    maxStockMilli: milliSchema,
    isAvailable: z.boolean(),
    updatedAt: timestampSchema,
});
/** Vista combinada que consume la caja: producto + precio + existencia. */
export const catalogItemSchema = productSchema.extend({
    categoryName: z.string().nullable(),
    categoryColor: z.string().nullable(),
    priceCents: centsSchema,
    /** `null` = sin mayoreo. Ver `storeProductSchema`. */
    wholesalePriceCents: centsSchema.nullable(),
    costCents: centsSchema,
    stockMilli: signedMilliSchema,
    minStockMilli: milliSchema,
    maxStockMilli: milliSchema,
    isAvailable: z.boolean(),
    barcodes: z.array(z.string()),
});
/**
 * De quien es un codigo de barras que ya esta dado de alta.
 *
 * Lo contesta `lookup_barcode_owner` y lo usa el alta para resolver el codigo
 * AL RECIBIRLO en vez de rechazarlo al final: escanear algo que ya se vende casi
 * siempre significa que llego mercancia, o sea que el camino no era dar de alta
 * sino RESURTIR.
 *
 * Los tres campos que deciden que ofrecer:
 *
 *   `isActive` false -> esta descontinuado. Aparta su codigo sin aparecer en el
 *                       inventario, que es lo que mas desconcierta; se reactiva.
 *   `inStore`  false -> es del negocio pero no de ESTA sucursal. El catalogo es
 *                       comun y la existencia no, asi que aqui no se puede
 *                       resurtir: entra por Traspaso.
 *   los dos si       -> se resurte, que es el caso comun.
 *
 * `stockMilli` y `priceCents` van en null cuando no esta en la sucursal, porque
 * ahi no existe el renglon que los guarda. NO viaja el costo: para pedir esto
 * basta `product:view` y el costo es de `inventory:view_cost`.
 */
export const barcodeOwnerSchema = z.object({
    /** La variante que choco, no la que se tecleo: si se capturo el UPC-A de 12
     *  digitos y lo guardado es el EAN-13, hay que enseñar el que existe. */
    code: z.string(),
    productId: idSchema,
    name: z.string(),
    sku: z.string(),
    brand: z.string().nullable(),
    unit: unitSchema,
    isWeighed: z.boolean(),
    isActive: z.boolean(),
    inStore: z.boolean(),
    stockMilli: signedMilliSchema.nullable(),
    priceCents: centsSchema.nullable(),
});
/**
 * Expediente de un producto, para decidir su baja.
 *
 * "Eliminar" son dos operaciones distintas y quien decide cual aplica es la
 * base, no la pantalla: un producto con ventas encima se DESCONTINUA (sale del
 * catalogo, su historia se queda, se puede reactivar) y uno que nunca se movio
 * se BORRA. Esto es lo que se consulta para plantear esa decision con numeros
 * en vez de con advertencias genericas.
 *
 * Los conteos son de toda la organizacion, no de la sucursal: el catalogo es
 * comun, asi que borrar en Centro tambien borra en Norte.
 */
export const productUsageSchema = z.object({
    productId: idSchema,
    name: z.string(),
    sku: z.string(),
    unit: unitSchema,
    isWeighed: z.boolean(),
    isActive: z.boolean(),
    /** Ventas en las que aparece. Las canceladas cuentan: siguen siendo historia. */
    salesCount: z.number().int().nonnegative(),
    lastSaleAt: timestampSchema.nullable(),
    purchaseCount: z.number().int().nonnegative(),
    /** Entradas de mercancia donde aparece, incluida la que espera firma. */
    intakeCount: z.number().int().nonnegative(),
    /**
     * Movimientos que involucran a OTRO —cliente o proveedor—: venta,
     * cancelacion, compra y devolucion. Son hechos del negocio y son los que
     * impiden borrar.
     */
    sharedMovementCount: z.number().int().nonnegative(),
    /**
     * Movimientos PROPIOS: conteo, merma y merma natural. Describen existencia
     * que se tecleo en casa y que nunca entro ni salio del negocio, asi que NO
     * impiden borrar — pero si se dicen, porque se van con el producto.
     */
    ownMovementCount: z.number().int().nonnegative(),
    /** Movimientos de kardex sin contar el alta, que nace y muere con el producto. */
    movementCount: z.number().int().nonnegative(),
    /** Existencia en ESTA sucursal, y lo que vale. El valor es 0 sin `inventory:view_cost`. */
    stockMilli: signedMilliSchema,
    stockValueCents: centsSchema,
    /*
     * Aqui vivian `otherStores` y `hiddenStoreCount`: que otras sucursales
     * manejaban el mismo producto. Se fueron el 22 de agosto de 2026 con el
     * catalogo por sucursal — un producto vive en UNA tienda, asi que la
     * pregunta ya no tiene respuesta posible que no sea la lista vacia.
     */
    hasHistory: z.boolean(),
    /** Permisos de quien pregunta, ya resueltos por la base. */
    mayWriteOff: z.boolean(),
    mayDelete: z.boolean(),
    /**
     * Si se puede borrar. Desde el 4 de septiembre de 2026 es lo MISMO que
     * `mayDelete`: el expediente ya no bloquea, solo cambia que significa borrar.
     * Se conservan los dos nombres porque responden preguntas distintas —"¿tengo
     * el permiso?" y "¿lo puedo hacer con ESTE producto?"— y el dia que vuelva a
     * haber un caso que se niegue, se niega aqui sin tocar la pantalla.
     */
    canDelete: z.boolean(),
    /**
     * QUE va a pasar al borrar. Lo decide la base mirando el expediente, no la
     * pantalla: en el navegador "no recuerdo haberlo vendido" no es un dato.
     *
     *   'clean'   Nunca se vendio ni se compro. Se va de raiz, con su kardex
     *             propio, como si nunca se hubiera dado de alta.
     *   'history' Ya movio dinero. Desaparece del catalogo y libera su clave y
     *             sus codigos, y las ventas cobradas quedan intactas: borrarlas
     *             haria que el total de aquella venta dejara de ser la suma de
     *             sus renglones.
     */
    deleteMode: z.enum(['clean', 'history']),
});
export const saleItemSchema = z.object({
    id: idSchema,
    saleId: idSchema,
    productId: idSchema,
    /** Nombre congelado al momento de la venta: el historico no cambia si se edita el catalogo. */
    nameSnapshot: z.string(),
    unitSnapshot: unitSchema,
    qtyMilli: milliSchema,
    unitPriceCents: centsSchema,
    unitCostCents: centsSchema,
    lineTotalCents: centsSchema,
    /**
     * Si este renglon se cobro a precio de mayoreo.
     *
     * Se guarda y NO se deduce: comparar `unitPriceCents` contra el precio de hoy
     * no sirve, porque los precios cambian y un ticket de hace un mes quedaria
     * reinterpretado. Sin esto, un renglon a $160 con el catalogo en $180 se lee
     * como un error de cobro, y la pregunta "cuanto vendi a mayoreo" no se puede
     * contestar nunca.
     */
    isWholesale: z.boolean(),
    /**
     * Si este renglon se cobro a un precio TECLEADO A MANO en la caja.
     *
     * Se guarda por la misma razon que `isWholesale`, y hace falta por una razon
     * mas: un precio manual no es ni el de lista ni el de mayoreo, asi que sin la
     * bandera es indistinguible de un error de captura seis meses despues, y la
     * pregunta "cuanto rebaje a mano" no se puede contestar nunca. Es lo unico
     * que hace que rebajar en el mostrador no contamine el margen en silencio.
     */
    isManualPrice: z.boolean(),
});
export const saleSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    userId: userIdSchema,
    cashSessionId: idSchema.nullable(),
    /** Consecutivo legible por sucursal, para que el cliente pueda referirlo. */
    folio: z.number().int().positive(),
    subtotalCents: centsSchema,
    discountCents: centsSchema,
    totalCents: centsSchema,
    /** Costo total de la mercancia vendida. Permite calcular utilidad sin recalcular. */
    costCents: centsSchema,
    paymentMethod: paymentMethodSchema,
    cashReceivedCents: centsSchema.nullable(),
    changeCents: centsSchema.nullable(),
    status: saleStatusSchema,
    note: z.string().max(280).nullable(),
    /** A quien se le fio. Nulo en cualquier otra forma de pago. */
    customerId: idSchema.nullable(),
    /**
     * Lo que el cliente dejo en efectivo al momento de fiar.
     *
     * No se guarda en la venta: se deriva del libro de credito, que es donde vive
     * la verdad de la deuda. Cero significa "no abono nada", no "no se sabe" —la
     * misma convencion que `costCents` para el costo que no se puede ver—.
     */
    downPaymentCents: centsSchema.default(0),
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
    cancelledAt: timestampSchema.nullable(),
});
export const saleWithItemsSchema = saleSchema.extend({
    items: z.array(saleItemSchema),
    userName: z.string(),
    customerName: z.string().nullable(),
});
export const inventoryMovementSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    productId: idSchema,
    type: movementTypeSchema,
    /** Delta con signo: negativo saca, positivo mete. */
    qtyDeltaMilli: signedMilliSchema,
    /** Existencia resultante tras aplicar el movimiento. Permite auditar sin recorrer todo. */
    balanceAfterMilli: signedMilliSchema,
    unitCostCents: centsSchema,
    reason: z.string().max(200).nullable(),
    refType: z.string().max(24).nullable(),
    refId: z.number().int().nullable(),
    userId: userIdSchema.nullable(),
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
});
export const supplierSchema = z.object({
    id: idSchema,
    name: trimmed(1, 80),
    contact: z.string().max(80).nullable(),
    phone: z.string().max(30).nullable(),
    /** Dias entre pedir y recibir. Alimenta el punto de reorden. */
    leadTimeDays: z.number().int().min(0).max(90),
    notes: z.string().max(400).nullable(),
    isActive: z.boolean(),
});
export const expenseSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    userId: userIdSchema,
    category: expenseCategorySchema,
    /** Opcional: null cuando la categoria y el importe ya lo dicen todo. */
    description: trimmed(1, 200).nullable(),
    amountCents: centsSchema,
    /** True si sale del cajon de la caja: afecta el corte. */
    paidFromCashDrawer: z.boolean(),
    recurrence: expenseRecurrenceSchema,
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
});
export const cashSessionSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    userId: userIdSchema,
    userName: z.string(),
    openingCents: centsSchema,
    /** Lo que el sistema calcula que deberia haber en el cajon. */
    expectedCents: signedCentsSchema.nullable(),
    /** Lo que el cajero conto fisicamente. */
    countedCents: centsSchema.nullable(),
    /** contado - esperado. Negativo es faltante. Se registra, no se esconde. */
    differenceCents: signedCentsSchema.nullable(),
    note: z.string().max(280).nullable(),
    businessDate: businessDateSchema,
    openedAt: timestampSchema,
    closedAt: timestampSchema.nullable(),
});
export const cashMovementSchema = z.object({
    id: idSchema,
    cashSessionId: idSchema,
    storeId: idSchema,
    userId: userIdSchema,
    type: cashMovementTypeSchema,
    amountCents: centsSchema,
    reason: trimmed(1, 200),
    createdAt: timestampSchema,
});
// ---------------------------------------------------------------------------
// Clientes y credito
// ---------------------------------------------------------------------------
export const customerSchema = z.object({
    id: idSchema,
    name: trimmed(1, 80),
    phone: z.string().max(30).nullable(),
    notes: z.string().max(400).nullable(),
    /** Limite acordado. Null = sin limite; 0 = no se le fia. */
    creditLimitCents: centsSchema.nullable(),
    /** Dias acordados para pagar cada cargo. */
    creditDays: z.number().int().min(0).max(365),
    isActive: z.boolean(),
    createdAt: timestampSchema,
});
export const CREDIT_MOVEMENT_TYPES = ['charge', 'payment', 'adjustment'];
export const creditMovementTypeSchema = z.enum(CREDIT_MOVEMENT_TYPES);
export const CREDIT_MOVEMENT_LABELS = {
    charge: 'Se le fio',
    payment: 'Abono',
    adjustment: 'Ajuste',
};
export const creditMovementSchema = z.object({
    id: idSchema,
    customerId: idSchema,
    storeId: idSchema,
    type: creditMovementTypeSchema,
    amountCents: centsSchema,
    /** Saldo del cliente tras aplicar el movimiento. Positivo = debe. */
    balanceAfterCents: signedCentsSchema,
    dueDate: businessDateSchema.nullable(),
    saleId: idSchema.nullable(),
    userId: userIdSchema,
    userName: z.string(),
    note: z.string().nullable(),
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
});
export const createCustomerRequestSchema = z.object({
    name: trimmed(1, 80),
    phone: z.string().trim().max(30).optional(),
    notes: z.string().trim().max(400).optional(),
    creditLimitCents: centsSchema.nullable().optional(),
    creditDays: z.number().int().min(0).max(365).default(15),
});
export const updateCustomerRequestSchema = z.object({
    name: trimmed(1, 80).optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    notes: z.string().trim().max(400).nullable().optional(),
    creditLimitCents: centsSchema.nullable().optional(),
    creditDays: z.number().int().min(0).max(365).optional(),
    isActive: z.boolean().optional(),
});
export const registerPaymentRequestSchema = z.object({
    storeId: idSchema,
    amountCents: centsSchema.refine((v) => v > 0, 'el abono debe ser mayor a cero'),
    note: z.string().trim().max(280).optional(),
    /** Si entra al cajon de la caja, afecta el corte. */
    intoCashDrawer: z.boolean().default(true),
    idempotencyKey: z.string().min(8).max(64),
});
export const creditAdjustmentRequestSchema = z.object({
    storeId: idSchema,
    /** Positivo aumenta la deuda, negativo la perdona. */
    deltaCents: signedCentsSchema.refine((v) => v !== 0, 'el ajuste no puede ser cero'),
    reason: trimmed(3, 280),
});
export const auditLogSchema = z.object({
    id: idSchema,
    storeId: idSchema.nullable(),
    userId: userIdSchema.nullable(),
    userName: z.string().nullable(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.number().int().nullable(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    ip: z.string().nullable(),
    createdAt: timestampSchema,
});
// ---------------------------------------------------------------------------
// Peticiones de la API
// ---------------------------------------------------------------------------
export const loginRequestSchema = z.object({
    username: trimmed(3, 32),
    password: z.string().min(1).max(200),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(8, 'la contraseña debe tener al menos 8 caracteres').max(200),
});
export const createSaleItemSchema = z.object({
    productId: idSchema,
    qtyMilli: milliSchema.refine((v) => v > 0, 'la cantidad debe ser mayor a cero'),
    /**
     * Precio manual. Solo se acepta si el usuario tiene permiso; en caso contrario
     * el servidor usa el precio de catalogo. Nunca se confia en el precio del cliente
     * sin revalidar quien lo manda.
     */
    overridePriceCents: centsSchema.optional(),
    /**
     * Cobrar este renglon a precio de mayoreo. Viaja la BANDERA, jamas el importe:
     * el precio lo lee `create_sale` de `store_products.wholesale_price_cents`.
     *
     * Es EXCLUYENTE con `overridePriceCents` y la base lo rechaza junto
     * (`VALIDATION_ERROR`): son dos respuestas a la misma pregunta —cuanto cuesta
     * este renglon— y adivinar cual gana seria cobrar lo que nadie decidio.
     */
    wholesale: z.boolean().optional(),
});
export const createSaleRequestSchema = z
    .object({
    storeId: idSchema,
    items: z.array(createSaleItemSchema).min(1, 'la venta necesita al menos un producto').max(200),
    paymentMethod: paymentMethodSchema,
    cashReceivedCents: centsSchema.optional(),
    discountCents: centsSchema.default(0),
    note: z.string().max(280).optional(),
    /** Obligatorio al fiar: sin cliente, la deuda no tiene a quien cobrarsele. */
    customerId: idSchema.optional(),
    /**
     * Autoriza rebasar el limite de credito del cliente por esta venta.
     * Requiere permiso; el servidor lo revalida y lo deja en auditoria.
     */
    creditOverride: z.boolean().default(false),
    /**
     * Lo que el cliente deja en efectivo AL FIAR. Fiar rara vez es todo o nada.
     *
     * Solo aplica a `credit`, y tiene que ser MENOR al total: si cubre el
     * ticket ya no es un fiado, es una venta de contado, y registrarla como
     * credito dejaria un cargo con saldo cero en la cartera.
     */
    paidNowCents: centsSchema.default(0),
    /**
     * Clave de idempotencia generada por el cliente. Si la caja pierde la
     * respuesta y reintenta, no se duplica la venta.
     */
    idempotencyKey: z.string().min(8).max(64),
})
    .refine((v) => v.paidNowCents === 0 || v.paymentMethod === 'credit', {
    message: 'el abono al momento solo existe al fiar',
    path: ['paidNowCents'],
})
    .refine((v) => v.paymentMethod !== 'cash' || v.cashReceivedCents !== undefined, {
    message: 'una venta en efectivo requiere el monto recibido',
    path: ['cashReceivedCents'],
})
    .refine((v) => v.paymentMethod !== 'credit' || v.customerId !== undefined, {
    message: 'elige a quien se le fia',
    path: ['customerId'],
});
export const cancelSaleRequestSchema = z.object({
    reason: trimmed(3, 200),
});
export const createProductRequestSchema = z.object({
    sku: trimmed(1, 40),
    name: trimmed(1, 120),
    brand: z.string().trim().max(60).optional(),
    categoryId: idSchema.optional(),
    unit: unitSchema,
    isWeighed: z.boolean().default(false),
    packSize: z.number().int().min(1).max(10_000).default(1),
    shrinkageBpMonthly: shrinkageBpSchema.default(0),
    scaleItemCode: z.string().trim().regex(/^\d{1,10}$/).optional(),
    barcodes: z.array(z.string().trim().min(4).max(48)).max(10).default([]),
    /** Alta inicial en la sucursal actual. */
    priceCents: centsSchema,
    costCents: centsSchema.default(0),
    initialStockMilli: milliSchema.default(0),
    minStockMilli: milliSchema.default(0),
    maxStockMilli: milliSchema.default(0),
});
export const updateProductRequestSchema = z.object({
    name: trimmed(1, 120).optional(),
    brand: z.string().trim().max(60).nullable().optional(),
    categoryId: idSchema.nullable().optional(),
    unit: unitSchema.optional(),
    isWeighed: z.boolean().optional(),
    packSize: z.number().int().min(1).max(10_000).optional(),
    shrinkageBpMonthly: shrinkageBpSchema.optional(),
    scaleItemCode: z.string().trim().regex(/^\d{1,10}$/).nullable().optional(),
    isActive: z.boolean().optional(),
    barcodes: z.array(z.string().trim().min(4).max(48)).max(10).optional(),
});
/** Vista previa del conteo fisico antes de guardarlo. */
export const countPreviewRequestSchema = z.object({
    productId: idSchema,
    countedStockMilli: signedMilliSchema,
});
export const updateStoreProductRequestSchema = z.object({
    priceCents: centsSchema.optional(),
    minStockMilli: milliSchema.optional(),
    maxStockMilli: milliSchema.optional(),
    isAvailable: z.boolean().optional(),
});
export const adjustStockRequestSchema = z.object({
    productId: idSchema,
    /** Existencia real contada fisicamente. El sistema calcula el delta. */
    countedStockMilli: signedMilliSchema,
    reason: trimmed(3, 200),
});
export const shrinkageRequestSchema = z.object({
    productId: idSchema,
    qtyMilli: milliSchema.refine((v) => v > 0, 'la cantidad debe ser mayor a cero'),
    reason: trimmed(3, 200),
});
export const createPurchaseItemSchema = z.object({
    productId: idSchema,
    qtyMilli: milliSchema.refine((v) => v > 0, 'la cantidad debe ser mayor a cero'),
    unitCostCents: centsSchema,
});
export const createPurchaseRequestSchema = z.object({
    storeId: idSchema,
    supplierId: idSchema.nullable().default(null),
    reference: z.string().trim().max(60).optional(),
    items: z.array(createPurchaseItemSchema).min(1).max(500),
    note: z.string().max(280).optional(),
    idempotencyKey: z.string().min(8).max(64),
});
/**
 * Acuse de una entrada de mercancia: lo que `create_purchase` contesta.
 *
 * Lo interesante no es el folio, es `newCostCents` de cada renglon. La hoja de
 * captura enseña una VISTA PREVIA del costo promedio mientras se teclea
 * (`weightedAvgCostCents`), pero el numero que vale es este, el que la base
 * escribio. Con el a la mano, el acuse puede decir cual producto se quedo con un
 * margen que ya no da y ofrecer corregir su precio en ese momento — que es el
 * unico momento en que alguien va a hacerlo.
 */
export const purchaseReceiptLineSchema = z.object({
    productId: idSchema,
    qtyMilli: milliSchema,
    unitCostCents: centsSchema,
    /** Costo promedio ponderado con el que quedo el producto en la sucursal. */
    newCostCents: centsSchema,
    /** Existencia despues de la entrada. */
    balanceAfterMilli: signedMilliSchema,
});
export const purchaseReceiptSchema = z.object({
    id: idSchema,
    folio: z.number().int().nonnegative(),
    totalCents: centsSchema,
    businessDate: businessDateSchema,
    /** True si esta entrada ya estaba registrada y se devolvio la original. */
    deduplicated: z.boolean(),
    lines: z.array(purchaseReceiptLineSchema),
});
/**
 * Una entrada anterior de UN producto, para tenerla enfrente al capturar la
 * siguiente.
 *
 * "La vez pasada te lo dieron a $120 hace 9 dias" es el dato que convierte la
 * captura en una revision: si hoy viene a $180 hay algo que preguntarle al
 * proveedor, y si son $12 es que alguien se equivoco de campo. Sale de
 * `purchase_items`, que solo alcanza quien tiene `purchase:manage`.
 */
export const productPurchaseSchema = z.object({
    purchaseId: idSchema,
    folio: z.number().int().nonnegative(),
    supplierName: z.string().nullable(),
    reference: z.string().nullable(),
    qtyMilli: milliSchema,
    unitCostCents: centsSchema,
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
});
/**
 * Una entrada de mercancia YA REGISTRADA, con sus renglones, para el historial.
 *
 * Existe porque `create_purchase` estaba completa y `cancel_purchase` tambien
 * —transaccion unica, kardex inverso, costo previo restaurado y auditoria— pero
 * la UNICA puerta para cancelar era el acuse que aparece al terminar de
 * capturar. En cuanto esa hoja se cerraba, una cifra mal tecleada quedaba
 * dentro para siempre: existencia de mas y, peor, un costo promedio ponderado
 * calculado sobre un numero que nunca ocurrio. Medido en la tienda real el 20
 * de agosto de 2026: 14 entradas registradas y CERO canceladas, no porque
 * ninguna estuviera bien sino porque no habia donde.
 *
 * Trae el desenlace completo —quien la registro, y si esta cancelada, quien y
 * por que— porque una entrada cancelada NO se borra: se marca. Un historial que
 * escondiera las canceladas contaria una historia distinta de la del kardex, y
 * el kardex es el que manda.
 */
export const purchaseHistoryLineSchema = z.object({
    productId: idSchema,
    /**
     * El nombre de HOY, leido de `products`, no uno congelado en la compra.
     *
     * Es lo contrario de `sale_items.name_snapshot`, y la diferencia no es un
     * descuido: un ticket es un documento que se le entrego a un cliente y tiene
     * que seguir diciendo lo que decia ese dia. Una entrada es un movimiento
     * interno del inventario, y quien la busca despues la busca por como se llama
     * el producto AHORA. Ademas es justo el caso que motiva todo esto: si el
     * nombre se capturo mal y se corrigio, el historial enseña el corregido.
     */
    name: z.string(),
    sku: z.string(),
    unit: unitSchema,
    isWeighed: z.boolean(),
    qtyMilli: milliSchema,
    unitCostCents: centsSchema,
    lineTotalCents: centsSchema,
});
export const purchaseHistorySchema = z.object({
    id: idSchema,
    folio: z.number().int().nonnegative(),
    status: z.enum(['completed', 'cancelled']),
    supplierName: z.string().nullable(),
    reference: z.string().nullable(),
    note: z.string().nullable(),
    totalCents: centsSchema,
    /** Quien la registro. Nulo si esa cuenta ya no existe. */
    userName: z.string().nullable(),
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
    cancelledAt: timestampSchema.nullable(),
    cancelledByName: z.string().nullable(),
    cancelReason: z.string().nullable(),
    /**
     * Folio de la entrada VIVA posterior que impide cancelar esta, o `null` si se
     * puede cancelar. Es el `PURCHASE_NOT_LATEST` de la base, anticipado.
     *
     * Se calcula aqui y no se descubre al pulsar por la misma razon por la que el
     * alta pregunta de quien es un codigo al recibirlo y no al final (§10): un
     * boton cuyo unico desenlace es el rechazo solo sirve para perder el viaje. Y
     * el calculo es EXACTO, no una aproximacion: la ventana se lee de la mas
     * nueva hacia atras, asi que cualquier entrada posterior a una de la lista
     * esta, por construccion, tambien en la lista.
     */
    supersededByFolio: z.number().int().nonnegative().nullable(),
    lines: z.array(purchaseHistoryLineSchema),
});
/**
 * Entradas de mercancia que esperan la firma de la dueña.
 *
 * Un LOTE es una entrega —un camion, una nota de proveedor— y al aprobarse sale
 * como un folio de compra. Esa equivalencia es lo que hace que "aceptar todas de
 * jalon" sea correcto y no una fusion de notas de proveedores distintos.
 *
 * Los campos de dinero llegan en CERO cuando quien pregunta no puede verlos, que
 * es la misma convencion del catalogo (§8): cero, no ausente, para que la
 * interfaz no tenga que aprender un caso nuevo. La linea es precisa —
 * `unitCostCents` y `lineTotalCents` los ve tambien QUIEN CAPTURO el lote,
 * porque los escribio el; `prevCostCents` y `priceCents` son el margen y esos
 * solo los ve `inventory:view_cost`.
 */
export const INTAKE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'];
export const intakeStatusSchema = z.enum(INTAKE_STATUSES);
export const INTAKE_STATUS_LABELS = {
    pending: 'Esperando aprobacion',
    approved: 'Aprobada',
    rejected: 'Rechazada',
    cancelled: 'Retirada',
};
export const stockIntakeLineSchema = z.object({
    id: idSchema,
    /** Null cuando el renglon propone un producto que no existe en el catalogo. */
    productId: idSchema.nullable(),
    name: z.string(),
    unit: unitSchema,
    isWeighed: z.boolean(),
    isNewProduct: z.boolean(),
    newSku: z.string().nullable(),
    newPackSize: z.number().int().positive().nullable(),
    newBarcode: z.string().nullable(),
    /** Producto que se creo al aprobar, si el renglon era una propuesta. */
    createdProductId: idSchema.nullable(),
    qtyMilli: milliSchema,
    /** Lo que dijo quien capturo que le cobraron. */
    unitCostCents: centsSchema,
    lineTotalCents: centsSchema,
    /** Lo que costaba antes de esta entrega. Null si es producto nuevo. */
    prevCostCents: centsSchema.nullable(),
    /** Precio de venta vigente, para calcular el margen que quedaria. */
    priceCents: centsSchema,
    stockMilli: signedMilliSchema.nullable(),
});
export const stockIntakeSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    status: intakeStatusSchema,
    submittedBy: z.string(),
    submittedByName: z.string().nullable(),
    submittedAt: timestampSchema,
    businessDate: businessDateSchema,
    supplierId: idSchema.nullable(),
    supplierName: z.string().nullable(),
    reference: z.string().nullable(),
    /** "¿Quien te lo indico?": la mitad de lo que la dueña va a verificar. */
    instructedBy: z.string().nullable(),
    note: z.string().nullable(),
    resolvedBy: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    resolvedAt: timestampSchema.nullable(),
    rejectReason: z.string().nullable(),
    purchaseId: idSchema.nullable(),
    purchaseFolio: z.number().int().nonnegative().nullable(),
    totalCents: centsSchema,
    lines: z.array(stockIntakeLineSchema),
});
/**
 * El resumen que encabeza la bandeja, ANTES de cualquier boton.
 *
 * Se calcula en la base y no en el navegador porque decide que se le enseña
 * primero a quien va a firmar: si nada cambio de costo y no hay productos
 * nuevos, aceptar el dia entero de un toque es razonable; si dos renglones
 * subieron de precio, hay que mirarlos.
 */
export const intakeQueueSummarySchema = z.object({
    batches: z.number().int().nonnegative(),
    lines: z.number().int().nonnegative(),
    totalCents: centsSchema,
    costChanged: z.number().int().nonnegative(),
    newProducts: z.number().int().nonnegative(),
    oldestWaitingMs: z.number().int().nonnegative(),
});
export const intakeQueueSchema = z.object({
    intakes: z.array(stockIntakeSchema),
    summary: intakeQueueSummarySchema,
});
/**
 * Lo que hizo cada quien, para la pantalla de Gestion de personal.
 *
 * Se mide POR AUTOR, no por turno, y eso no contradice el invariante del corte:
 * para cuadrar un cajon manda el turno —"lo que pasa en mi caja es mio aunque lo
 * capture otro"—, pero aqui la pregunta es literalmente quien hizo que. Por eso
 * las ventas del empleado salen a su nombre aunque el efectivo haya caido en el
 * cajon de la caja.
 */
export const staffMemberActivitySchema = z.object({
    userId: z.string(),
    name: z.string(),
    username: z.string().nullable(),
    role: roleSchema,
    isActive: z.boolean(),
    salesCount: z.number().int().nonnegative(),
    salesCents: centsSchema,
    cancelledCount: z.number().int().nonnegative(),
    shifts: z.number().int().nonnegative(),
    expensesCount: z.number().int().nonnegative(),
    expensesCents: centsSchema,
    intakesPending: z.number().int().nonnegative(),
    intakesApproved: z.number().int().nonnegative(),
    intakesRejected: z.number().int().nonnegative(),
    /** 0 cuando esa persona no ha hecho nada en la ventana. */
    lastSeenAt: timestampSchema,
});
export const staffActivitySchema = z.object({
    fromDate: businessDateSchema,
    days: z.number().int().positive(),
    staff: z.array(staffMemberActivitySchema),
});
/**
 * Los avisos de la campanita.
 *
 * QUIEN VE CADA UNO NO SE DECIDE AQUI. Cada aviso guarda en la base el permiso
 * que hay que tener para verlo y `get_notifications` ya devuelve solo lo que a
 * esta persona le toca; el frontend no filtra nada. Esta lista de tipos existe
 * para lo unico que si es de la interfaz: que simbolo dibuja, de que color, y a
 * que pantalla lleva al tocarlo.
 *
 * El TEXTO viene redactado desde la base. Un aviso que se armara aqui diria hoy
 * algo distinto de lo que decia cuando ocurrio —"quedan 3 kg" es cierto en el
 * momento del cruce y mentira una hora despues—, asi que `title` y `body` se
 * escriben al emitir y ya no se tocan.
 */
export const NOTIFICATION_KINDS = [
    /** El corte del dia, a las 18:15 de la tienda. */
    'daily_report',
    /** Alguien mando una entrada de mercancia y espera firma. */
    'intake_submitted',
    /** Firmaron o rechazaron la que uno mando. Va dirigido a esa persona. */
    'intake_resolved',
    /** Un producto cruzo por debajo de su minimo. */
    'low_stock',
    /** Un producto se acabo. */
    'out_of_stock',
];
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
export const NOTIFICATION_SEVERITIES = ['info', 'warn', 'critical'];
export const notificationSeveritySchema = z.enum(NOTIFICATION_SEVERITIES);
export const notificationSchema = z.object({
    id: idSchema,
    kind: notificationKindSchema,
    severity: notificationSeveritySchema,
    title: z.string(),
    body: z.string(),
    /**
     * Lo que la pantalla necesita para abrir el destino: `date` del reporte,
     * `intakeId` de la entrega, `productId` del desabasto. Es `unknown` porque
     * cambia con el tipo; quien lo consume lo estrecha.
     */
    payload: z.record(z.string(), z.unknown()),
    businessDate: businessDateSchema,
    createdAt: timestampSchema,
    read: z.boolean(),
});
export const notificationFeedSchema = z.object({
    notifications: z.array(notificationSchema),
    /**
     * TODOS los que estan sin leer, no solo los que caben en la primera pagina:
     * es lo que dice la burbuja, y una burbuja que dice "9" porque el limite es 9
     * deja de significar algo.
     */
    unreadCount: z.number().int().nonnegative(),
});
export const createExpenseRequestSchema = z.object({
    storeId: idSchema,
    category: expenseCategorySchema,
    description: trimmed(1, 200).optional(),
    amountCents: centsSchema.refine((v) => v > 0, 'el importe debe ser mayor a cero'),
    paidFromCashDrawer: z.boolean().default(true),
    recurrence: expenseRecurrenceSchema.default('one_time'),
});
export const openCashSessionRequestSchema = z.object({
    storeId: idSchema,
    openingCents: centsSchema,
    note: z.string().max(280).optional(),
});
export const closeCashSessionRequestSchema = z.object({
    countedCents: centsSchema,
    note: z.string().max(280).optional(),
});
export const cashMovementRequestSchema = z.object({
    type: cashMovementTypeSchema,
    amountCents: centsSchema.refine((v) => v > 0, 'el importe debe ser mayor a cero'),
    reason: trimmed(3, 200),
});
export const createUserRequestSchema = z.object({
    username: trimmed(3, 32).regex(/^[a-z0-9._-]+$/i, 'solo letras, numeros, punto, guion y guion bajo'),
    fullName: trimmed(1, 80),
    password: z.string().min(8, 'la contraseña debe tener al menos 8 caracteres').max(200),
    memberships: z
        .array(z.object({ storeId: idSchema, role: roleSchema }))
        .min(1, 'asigna al menos una sucursal'),
});
export const createStoreRequestSchema = z.object({
    name: trimmed(1, 80),
    code: trimmed(1, 16).regex(/^[A-Z0-9-]+$/i, 'solo letras, numeros y guion'),
    address: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(30).optional(),
    timezone: z.string().min(1).max(64).default('America/Mexico_City'),
    dayStartHour: z.number().int().min(0).max(23).default(0),
    cashRoundingCents: z.number().int().min(0).max(1000).default(0),
});
// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------
export const dateRangeQuerySchema = z.object({
    from: businessDateSchema.optional(),
    to: businessDateSchema.optional(),
});
/**
 * Booleano que llega por query string.
 *
 * NO se usa `z.coerce.boolean()`: aplica `Boolean(valor)`, de modo que la cadena
 * "false" se convierte en `true`. Es un error silencioso clasico y aqui haria
 * que un filtro quedara siempre encendido.
 */
export const queryBooleanSchema = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true' || v === '1'));
export const catalogQuerySchema = z.object({
    search: z.string().trim().max(80).optional(),
    categoryId: z.coerce.number().int().positive().optional(),
    onlyLowStock: queryBooleanSchema.optional(),
    includeInactive: queryBooleanSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const lookupQuerySchema = z.object({
    barcode: z.string().trim().min(1).max(48),
});
// ---------------------------------------------------------------------------
// Eventos de tiempo real
// ---------------------------------------------------------------------------
export const REALTIME_EVENTS = [
    'sale.created',
    'sale.cancelled',
    'stock.changed',
    'expense.created',
    'expense.deleted',
    'cash.opened',
    'cash.closed',
    'cash.movement',
    'purchase.created',
    'product.changed',
    'credit.changed',
    'customer.changed',
];
export const realtimeEventSchema = z.object({
    event: z.enum(REALTIME_EVENTS),
    storeId: idSchema,
    at: timestampSchema,
    payload: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Utilidades derivadas
// ---------------------------------------------------------------------------
/** True si la unidad implica cantidades con decimales. */
export function unitIsWeighed(unit) {
    return WEIGHED_UNITS.includes(unit);
}
//# sourceMappingURL=schemas.js.map